import { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError, STATUS_LABELS, STATUS_ORDER, STATUS_STYLE } from "@/lib/api";
import { StatusPill } from "@/components/StatusPill";
import { Plus, Search, ChevronDown } from "lucide-react";
import { toast } from "sonner";

export default function RepairOrders({ mineOnly = false }) {
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [q, setQ] = useState("");
  // Iter 108 — Debounce + AbortController para búsqueda parcial en tiempo real
  const [loading, setLoading] = useState(false);
  const abortRef = useRef(null);
  const debounceRef = useRef(null);

  const load = async (queryStr) => {
    if (abortRef.current) {
      try { abortRef.current.abort(); } catch { /* noop */ }
    }
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    setLoading(true);
    try {
      const params = { q: (queryStr ?? q ?? "").trim() };
      if (mineOnly) params.mine = true;
      const r = await api.get("/repairs", { params, signal: ctrl.signal });
      setOrders(r.data);
    } catch (err) {
      // AbortError es esperado cuando el usuario sigue escribiendo — silencioso.
      if (err?.name !== "CanceledError" && err?.code !== "ERR_CANCELED") {
        console.warn("[RepairOrders] search error:", err);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    // Iter 108 — Debounce 150 ms para no saturar al backend con cada tecla.
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => load(q), 150);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line
  }, [q, mineOnly]);

  const changeStatus = async (id, status) => {
    try {
      await api.put(`/repairs/${id}`, { status });
      load(q);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const byStatus = (s) => orders.filter((o) => o.status === s);
  // Vista Kanban (acordeón vertical): solo estados activos. Entregado, "listo sin reparación"
  // y cancelado siguen existiendo en el sistema pero no aparecen aquí.
  const ACTIVE_STATUSES = ["recibido", "en_diagnostico", "en_reparacion", "listo_para_entregar"];
  // Iter 108 — Cuando el usuario busca activamente, mostramos también los
  // estados inactivos (entregado, cancelado, etc.) para no ocultar resultados
  // válidos. Esto evita el bug de "no encuentro esa reparación" cuando ya
  // se marcó como entregada.
  const searchActive = !!(q || "").trim();
  const statusesToShow = searchActive
    ? STATUS_ORDER.filter((s) => byStatus(s).length > 0)
    : ACTIVE_STATUSES;

  return (
    <div className="p-6 lg:p-10">
      {/* Hero header */}
      <header className="flex items-end justify-between pb-6 gap-4 flex-wrap border-b border-dashed border-[#D5D7CE]">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-[.14em] text-[#3F5222] font-semibold">
            <span className="text-[#8A9098]">//</span>&nbsp;&nbsp;{mineOnly ? "Mi cola" : "Workshop"}
          </div>
          <h1 className="font-display text-4xl lg:text-[38px] font-black mt-2 tracking-tight">
            {mineOnly ? "Mis reparaciones" : "Reparaciones"}
          </h1>
          {mineOnly && (
            <p className="text-sm text-[#4B5158] mt-1">
              Solo se muestran las órdenes asignadas a ti.
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Link
            to="/repairs/new"
            data-testid="repair-new"
            className="ds-btn primary"
          >
            <Plus size={15} /> Nueva orden
          </Link>
        </div>
      </header>

      <div className="mt-6 flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#525252]" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => {
              // Iter 108 — Enter: si hay coincidencia exacta con un código de
              // reparación, navegar directo a la ficha para agilizar el trabajo.
              if (e.key !== "Enter") return;
              const needle = (q || "").trim().toUpperCase();
              if (!needle) return;
              const exact = orders.find(
                (o) => (o.code || "").toUpperCase() === needle
                    || (o.code || "").toUpperCase() === `REP-${needle}`,
              );
              if (exact) {
                e.preventDefault();
                navigate(`/repairs/${exact.id}`);
              }
            }}
            placeholder="Buscar por cliente, código REP-XXXX, número, S/N, equipo..."
            className="w-full ds-card focus:border-[#6E8B3D] focus:ring-1 focus:ring-[#6E8B3D] outline-none pl-9 pr-3 py-2 text-sm rounded-none"
            data-testid="repair-search"
          />
        </div>
        <div className="text-xs font-mono uppercase tracking-widest text-[#525252]" data-testid="repair-search-count">
          {(() => {
            // Iter 108 (fix cosmético del contador): cuando NO hay búsqueda
            // activa mostramos solo el conteo de reparaciones activas visibles;
            // cuando SÍ hay búsqueda, el contador refleja todos los resultados
            // (incluidos entregado/cancelado si aparecen).
            const visibleCount = searchActive
              ? orders.length
              : orders.filter((o) => ACTIVE_STATUSES.includes(o.status)).length;
            if (loading) return "Buscando…";
            const label = `${visibleCount} ${visibleCount === 1 ? "orden" : "órdenes"}`;
            return (
              <>
                {label}
                {q.trim() && orders.length === 0 && (
                  <span className="ml-2 text-amber-700">· sin resultados</span>
                )}
              </>
            );
          })()}
        </div>
      </div>

      <div className="mt-6 space-y-3" data-testid="repairs-accordion">
        {statusesToShow.map((s) => (
          <StatusAccordion
            key={s}
            status={s}
            items={byStatus(s)}
            onChangeStatus={changeStatus}
            searchActive={searchActive}
          />
        ))}
      </div>
    </div>
  );
}

function StatusAccordion({ status, items, onChangeStatus, searchActive = false }) {
  // Iter 96 — Todos los acordeones cerrados por defecto al entrar en Reparaciones.
  // No se recuerda el estado de la sesión anterior; el usuario decide qué abrir.
  const [open, setOpen] = useState(false);
  // Cuando el usuario escribe en el buscador, abrir automáticamente los
  // acordeones que contienen resultados para que los vea sin tener que
  // desplegarlos manualmente. Al borrar la búsqueda, se cierran de nuevo.
  useEffect(() => {
    if (searchActive && items.length > 0) {
      setOpen(true);
    } else if (!searchActive) {
      setOpen(false);
    }
  }, [searchActive, items.length]);
  return (
    <div className="ds-card" data-testid={`accordion-${status}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-4 py-3 border-b border-[#E5E5E0] hover:bg-[#F7F7F4] transition-colors"
        data-testid={`accordion-toggle-${status}`}
        aria-expanded={open}
      >
        <div className="flex items-center gap-3">
          <ChevronDown
            size={16}
            className={`text-[#525252] transition-transform ${open ? "" : "-rotate-90"}`}
          />
          <StatusPill status={status} />
          <span className="font-mono text-xs text-[#525252]" data-testid={`accordion-count-${status}`}>
            {items.length} {items.length === 1 ? "reparación" : "reparaciones"}
          </span>
        </div>
      </button>
      {open && (
        <div>
          {items.length === 0 ? (
            <div className="px-4 py-6 text-center text-xs text-[#525252]">Sin órdenes</div>
          ) : (
            <ul className="divide-y divide-[#E5E5E0]">
              {items.map((o) => (
                <RepairListRow key={o.id} order={o} onChangeStatus={onChangeStatus} />
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function RepairListRow({ order, onChangeStatus }) {
  const navigate = useNavigate();
  return (
    <li
      onClick={() => navigate(`/repairs/${order.id}`)}
      className="px-4 py-2.5 hover:bg-[#F7F7F4] cursor-pointer transition-colors flex flex-col md:flex-row md:items-center gap-2 md:gap-4"
      data-testid={`repair-row-${order.code}`}
    >
      <span className="font-mono text-[11px] text-[#525252] md:w-24 shrink-0">{order.code}</span>
      <span className="font-semibold text-sm md:w-56 md:shrink-0 truncate">{order.customer_name}</span>
      <span className="text-xs text-[#525252] md:w-48 md:shrink-0 truncate">
        {order.device_type}{order.brand ? ` · ${order.brand}` : ""}{order.model ? ` ${order.model}` : ""}
      </span>
      <span className="text-xs text-[#404040] flex-1 truncate">{order.issue || "—"}</span>
      <span className="font-mono text-xs text-[#111110] md:w-20 md:text-right shrink-0">
        €{(order.estimated_cost || 0).toFixed(2)}
      </span>
      <select
        value={order.status}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => {
          e.preventDefault();
          onChangeStatus(order.id, e.target.value);
        }}
        data-testid={`repair-status-select-${order.code}`}
        className={`px-2 py-1 text-[10px] font-mono uppercase tracking-wider border rounded-none md:w-48 shrink-0 ${
          STATUS_STYLE[order.status]
        }`}
      >
        {STATUS_ORDER.map((s) => (
          <option key={s} value={s}>
            {STATUS_LABELS[s]}
          </option>
        ))}
      </select>
    </li>
  );
}
